import { useCallback, useRef } from "react";

/// 讓橫向捲動的 tab 列/選擇框支援「按住滑鼠拖曳」來捲動。單純的
/// overflow-x:auto 只吃觸控板滑動手勢跟滾輪，桌機滑鼠使用者第一直覺
/// 通常是「抓住拖曳」（像拖輪播圖那樣），對純 CSS overflow 完全沒反應，
/// 使用者會誤以為整條被截斷的內容完全滑不動。
///
/// 拖曳距離超過門檻才視為真正在拖曳，並在 capture 階段吃掉隨後的
/// click，避免放開滑鼠時誤觸底下 tab 按鈕的 onClick——單純點擊（沒有
/// 明顯移動）則完全不受影響，正常觸發按鈕。
///
/// 門檻不用固定死的 px：改用「按下當下容器的高度」去換算（取一半），
/// 容器越高（按鈕/列越大），代表點擊目標也越大，手指/滑鼠在點擊瞬間
/// 自然抖動的容許範圍就該跟著放大，不然矮小的門檻在正常點擊時反而常把
/// click 誤判成拖曳而吞掉。太矮的容器（理論上不太會發生）用 8px 兜底。
export function useDragScroll() {
  const stateRef = useRef({ isDown: false, startX: 0, startScrollLeft: 0, moved: false, threshold: 8 });

  const onMouseDown = useCallback((e) => {
    const el = e.currentTarget;
    stateRef.current = {
      isDown: true,
      startX: e.pageX,
      startScrollLeft: el.scrollLeft,
      moved: false,
      threshold: Math.max(8, el.getBoundingClientRect().height / 2),
    };
  }, []);

  const onMouseMove = useCallback((e) => {
    const st = stateRef.current;
    if (!st.isDown) return;
    const el = e.currentTarget;
    const delta = e.pageX - st.startX;
    if (Math.abs(delta) > st.threshold) st.moved = true;
    el.scrollLeft = st.startScrollLeft - delta;
  }, []);

  const endDrag = useCallback(() => {
    stateRef.current.isDown = false;
  }, []);

  const onClickCapture = useCallback((e) => {
    if (stateRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return {
    onMouseDown,
    onMouseMove,
    onMouseUp: endDrag,
    onMouseLeave: endDrag,
    onClickCapture,
  };
}
